import { useEffect, useState } from 'react';

import { resolve } from '@/api/endPoints';
import axiosClient from '@/utils/axios';

/** Boolean features a social media management plan can unlock. */
export type PlanFeature = 'overlay_templates';

export interface PlanFeatureState {
  isAvailable: boolean;
  isLoading: boolean;
}

/**
 * Reads one boolean feature off the account's current subscription.
 *
 * The backend is the only authority on what a plan includes — it also applies
 * the per-account exceptions Support hands out — so the answer is fetched from
 * `current_subscription` rather than inferred here. While it is in flight the
 * feature counts as unavailable, so a gated screen never flashes into view for
 * an account that cannot use it.
 */
export const usePlanFeature = (feature: PlanFeature): PlanFeatureState => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadFeature = async () => {
      try {
        const response = await axiosClient.get(
          resolve('current_subscription', {}, {})
        );
        if (cancelled) return;
        setIsAvailable(
          Boolean(
            response.data?.social_media_management_subscription?.[feature]
          )
        );
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load the current subscription', error);
        setIsAvailable(false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadFeature();

    return () => {
      cancelled = true;
    };
  }, [feature]);

  return { isAvailable, isLoading };
};
